import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';

import * as api from '@/features/backlog/api';
import { Watch } from '@/features/backlog/types';
import { useIdentity } from '@/features/identity/identity';

type WatchType = 'item' | 'board';
interface WatchesCtx {
  watches: Watch[];
  isWatching: (type: WatchType, id: string) => boolean;
  toggle: (type: WatchType, id: string) => void;
  reload: () => void;
}

const Ctx = createContext<WatchesCtx | null>(null);
export const useWatches = (): WatchesCtx | null => useContext(Ctx);

const keyOf = (t: string, id: string) => `${t}:${id}`;

/** App-wide watch subscriptions: which items/boards the member follows for the hourly email digest.
 *  Loaded once, toggled optimistically. */
export function WatchesProvider({ children }: { children: ReactNode }) {
  const { me } = useIdentity();
  const [watches, setWatches] = useState<Watch[]>([]);
  const [set, setSet] = useState<Set<string>>(new Set());

  const reload = useCallback(() => {
    if (!me) return;
    api
      .listMyWatches()
      .then((ws) => {
        setWatches(ws);
        setSet(new Set(ws.map((w) => keyOf(w.target_type, w.target_id))));
      })
      .catch(() => undefined);
  }, [me]);

  useEffect(() => {
    reload();
  }, [reload]);

  const isWatching = useCallback((type: WatchType, id: string) => set.has(keyOf(type, id)), [set]);
  const toggle = useCallback(
    (type: WatchType, id: string) => {
      const k = keyOf(type, id);
      const next = !set.has(k);
      setSet((s) => {
        const n = new Set(s);
        if (next) n.add(k);
        else n.delete(k);
        return n;
      });
      api.setWatch(type, id, next).then(reload).catch(reload);
    },
    [set, reload],
  );

  return <Ctx.Provider value={{ watches, isWatching, toggle, reload }}>{children}</Ctx.Provider>;
}
